import type { SobQuizProps, SurveyProps } from "@jti/ui";

export type CheckoutConfigurationTaxInfo = {
  message: string;
  visible: boolean;
};

export type CheckoutConfigurationFailedPaymentFlow = {
  title: string;
  message: string;
  retryPaymentButtonLabel: string;
};

export type CheckoutConfigurationContactConfig = {
  differentRecipientDisabled?: boolean;
  populatedPhoneInput?: boolean;
};

export type CheckoutConfigurationSubscriptionTermsPlacement = "sidebar" | "consentAndPaySection";

export type CheckoutConfigurationCartOverviewTierBenefit = {
  icon: string;
  text: string;
};

export type CheckoutConfigurationCartOverviewTierBenefits = {
  title?: string;
  items?: CheckoutConfigurationCartOverviewTierBenefit[];
};

export type CheckoutConfigurationCartOverviewTier = {
  enabled: boolean;
  title?: string;
  descriptionCollapsed?: string;
  descriptionExpanded?: string;
  pointsLabel: string;
  collectMessage: string;
  knowMoreLabel?: string;
  knowMoreUrl?: string;
  statusMessages: {
    statusMessageUpgrade: string;
    statusMessageRetention: string;
    statusMessageDowngrade: string;
    statusMessageDowngradeRetained: string;
    statusMessageMax: string;
  };
  showBenefits?: boolean;
  goldBenefits?: CheckoutConfigurationCartOverviewTierBenefits;
  blackBenefits?: CheckoutConfigurationCartOverviewTierBenefits;
};

export type StoreFilteringByAttributes = "flavor_label" | "color_label" | "both";

export type DeliveryOption = {
  description?: string;
  flotiqApiKey?: string;
  flotiqContentType?: string;
  defaultDelivery: boolean;
  label: string;
  lat?: string;
  lon?: string;
  methodCode: string;
  enableStoreFilteringByAttributes?: StoreFilteringByAttributes | "" | undefined;
  storeLocatorShopDetailsPage?: string;
  widgetTagName?: string;
  widgetUrl?: string;
  widgetVariant?: "embedded" | "modal";
  defaultZoom?: string;
  storeCardAddressEnabled?: boolean;
  openingHoursEnabled?: boolean;
  openingHoursBreaksEnabled?: boolean;
  availableSticksEnabled?: boolean;
  availableDevicesEnabled?: boolean;
  filtersEnabled?: boolean;
};

export type CheckoutOrderSummarySocialMedia = {
  icon: string;
  uri: string;
};

export type CheckoutOrderSummaryConfig = {
  displayItemsSummary?: boolean;
  displayShippingAddress?: boolean;
  displayDeliveryMethod?: boolean;
  displayBillingAddress?: boolean;
  displayPaymentMethod?: boolean;
  displayLoyaltyConfirmation?: boolean;
  displayAdditionalInfo?: boolean;
  additionalInfo?: {
    title?: string;
    message?: string;
  };
  displaySocialMedia?: boolean;
  socialMediaTitle?: string;
  socialMedia?: CheckoutOrderSummarySocialMedia[];
  withdrawOrderInfo?: string;
};

export type CheckoutConfiguration = {
  payOnDelivery: {
    paymentMethod: string;
  };
  zeroValueOrder: {
    userNotification: string;
  };
  loyalty: {
    learnMoreUrl: string;
    orderConfirmationLoyaltyUrl: string;
    redemptionTabSymbol: string;
    showMgm: boolean;
    showReminderDialog: boolean;
    reminderDialogTitle: string;
    reminderDialogSubtitle: string;
    reminderDialogText: string;
    reminderDialogSecondaryButtonText: string;
    reminderDialogSecondaryButtonLink: string;
    reminderDialogSKUs?: string;
  };
  cartOverviewTier?: CheckoutConfigurationCartOverviewTier;
  taxInfo?: CheckoutConfigurationTaxInfo;
  failedPaymentFlow?: CheckoutConfigurationFailedPaymentFlow;
  contactConfig?: CheckoutConfigurationContactConfig;
  discountDisclaimer: {
    enabled: boolean;
    enabledWhenZeroValueDeposit?: boolean;
    disclaimerMessage: string;
    warningMessage: string;
  };
  ageVerificationPopup?: {
    description: string;
    successMessage: string;
    popupMode: boolean;
  };
  consentsConfig?: {
    policyAndTermsConsentDisabled?: boolean;
  };
  subscriptionTermsPlacement?: CheckoutConfigurationSubscriptionTermsPlacement | null;
  ageVerificationMethodImages?: Record<string, string>;
  deliveryOptions: DeliveryOption[];
  survey?: SurveyProps;
  sob?: SobQuizProps;
  orderSummaryConfig: CheckoutOrderSummaryConfig;
  fastFlowEnabled?: boolean;
  consentReminderPopup?: {
    consentReminderPopupEnabled: boolean;
    maximumOccurancesPerYear: number;
    consentExpirationLimit: number;
    popUpDelay: number;
    offerConsentName: string;
    productInformationConsentName: string;
    ploomClubConsentName: string;
    mainImage: string;
    imageAltText: string;
    subtitle: string;
    mainQuestion: string;
    description: string;
    yesButtonText: string;
    noButtonText: string;
    yesRedirectUrl: string;
  };
};
